/**
 * AWS Amplify Configuration for Cognito Authentication
 *
 * Config can come from:
 * 1. VITE_ env vars (if set in frontend/.env or build)
 * 2. Backend API /api/auth/cognito-config (e.g. when only root .env has COGNITO_*)
 */

export interface CognitoConfigShape {
  region: string;
  userPoolId: string;
  userPoolClientId: string;
}

/** Build Amplify Auth config from Cognito values */
export function buildAmplifyConfig(config: CognitoConfigShape) {
  return {
    Auth: {
      Cognito: {
        region: config.region || 'us-east-1',
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
      },
    },
  };
}

/** Config from env (VITE_*). Empty userPoolId means not configured. */
export const awsConfigFromEnv = buildAmplifyConfig({
  region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
});

/** True if Cognito is configured via env (so we don't need to fetch from backend) */
export function hasCognitoEnvConfig(): boolean {
  const id = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  return typeof id === 'string' && id.length > 0;
}

/**
 * Get Cognito configuration from backend API.
 * Use this when VITE_COGNITO_* are not set (e.g. only root .env has COGNITO_*).
 */
export async function fetchCognitoConfig(): Promise<CognitoConfigShape> {
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const response = await fetch(`${apiUrl}/api/auth/cognito-config`);

  if (!response.ok) {
    throw new Error('Failed to fetch Cognito configuration');
  }

  return response.json();
}
